
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Helmet } from "react-helmet-async";

import {
  ArrowRight,
  ArrowUpRight,
  BookOpen,
  CalendarDays,
  Check,
  ChevronRight,
  Compass,
  Hotel,
  MapPin,
  Search,
  Sparkles,
  X,
  LoaderCircle,
  AlertCircle,
  RotateCcw,
} from "lucide-react";

import blogImg from "../assests/images/blog-bg.jpg";

/* ============================================================
   CONFIGURATION
============================================================ */

const API_URL = "https://dubaibudgetivehotels.com/api";
const BLOG_IMAGE_BASE = "https://dubaibudgetivehotels.com/uploads/blogs/";

const HERO_IMAGE =
  "https://images.unsplash.com/photo-1512453979798-5ea266f8880c?auto=format&fit=crop&w=1600&q=85";

const HOTEL_IMAGE =
  "https://images.unsplash.com/photo-1566073771259-6a8506099945?auto=format&fit=crop&w=1600&q=85";

/* ============================================================
   HELPERS
============================================================ */

function formatDate(date) {
  if (!date) return "";

  const parsed = new Date(date);

  if (Number.isNaN(parsed.getTime())) return "";

  return parsed.toLocaleDateString("en-US", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function getImage(blog) {
  const image = blog?.image;

  if (!image || typeof image !== "string") {
    return blogImg;
  }

  if (/^https?:\/\//i.test(image)) {
    return image;
  }

  if (image.startsWith("//")) {
    return `https:${image}`;
  }

  if (image.startsWith("/")) {
    return `https://dubaibudgetivehotels.com${image}`;
  }

  return `${BLOG_IMAGE_BASE}${image}`;
}

function plainText(value) {
  return String(value || "")
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/\s+/g, " ")
    .trim();
}

function getDescription(blog) {
  return (
    plainText(blog?.excerpt || blog?.description) ||
    "Discover travel inspiration, Dubai destinations, and helpful tips for your next stay."
  );
}

function getBlogsFromResponse(response) {
  const payload = response?.data;
  const value = payload?.blogs ?? payload?.data ?? payload;

  if (Array.isArray(value)) return value;

  if (Array.isArray(value?.data)) return value.data;

  return [];
}

/* ============================================================
   SECTION HEADING
============================================================ */

function SectionHeading({
  eyebrow,
  title,
  description,
  right,
}) {
  return (
    <div className="flex flex-col justify-between gap-5 sm:flex-row sm:items-end">
      <div className="max-w-[730px]">
        <div className="mb-4 flex items-center gap-3">
          <span className="h-[2px] w-8 bg-[#168B43]" />

          <p className="text-[11px] font-extrabold uppercase tracking-[0.2em] text-[#168B43]">
            {eyebrow}
          </p>
        </div>

        <h2 className="text-[32px] font-extrabold leading-[1.15] tracking-[-0.04em] text-[#17324D] sm:text-[43px]">
          {title}
        </h2>

        {description && (
          <p className="mt-4 max-w-[600px] text-sm leading-7 text-[#718096]">
            {description}
          </p>
        )}
      </div>

      {right}
    </div>
  );
}

/* ============================================================
   BLOG CARD
============================================================ */

function BlogCard({ blog }) {
  return (
    <Link
      to={`/blogs/${blog.slug}`}
      className="group flex h-full flex-col overflow-hidden rounded-[20px] border border-[#E3ECE6] bg-white shadow-[0_5px_25px_rgba(6,78,59,0.035)] transition-all duration-300 hover:-translate-y-1 hover:border-[#B9DDC7] hover:shadow-[0_18px_45px_rgba(6,78,59,0.10)]"
    >
      <div className="relative h-[235px] overflow-hidden bg-[#E8F0EB] sm:h-[255px]">
        <img
          src={getImage(blog)}
          alt={blog.title || "Dubai travel article"}
          loading="lazy"
          onError={(event) => {
            event.currentTarget.onerror = null;
            event.currentTarget.src = blogImg;
          }}
          className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110"
        />

        <div className="absolute inset-0 bg-gradient-to-t from-[#064E3B]/35 to-transparent" />

        <span className="absolute left-5 top-5 rounded-full bg-white/95 px-4 py-2 text-[10px] font-extrabold uppercase tracking-wider text-[#168B43] shadow-sm">
          {blog.category?.name || "Travel Guide"}
        </span>

        <span className="absolute bottom-5 right-5 flex h-11 w-11 items-center justify-center rounded-full bg-white text-[#168B43] shadow-md transition group-hover:bg-[#168B43] group-hover:text-white">
          <ArrowUpRight size={19} />
        </span>
      </div>

      <div className="flex flex-1 flex-col p-6 sm:p-7">
        <div className="flex flex-wrap items-center gap-3 text-[11px] font-semibold text-[#8492A6]">
          <span className="flex items-center gap-2">
            <CalendarDays size={14} className="text-[#168B43]" />
            {formatDate(blog.created_at) || "Travel Article"}
          </span>
        </div>

        <h3 className="mt-4 line-clamp-2 text-[21px] font-extrabold leading-[1.4] tracking-tight text-[#17324D] transition group-hover:text-[#168B43] sm:text-[23px]">
          {blog.title}
        </h3>

        <p className="mt-3 line-clamp-3 text-[13px] leading-7 text-[#718096]">
          {getDescription(blog)}
        </p>

        <div className="mt-auto flex items-center justify-between border-t border-[#E8F0EB] pt-5 text-[12px] font-extrabold text-[#168B43]">
          <span>Read Full Article</span>
          <ArrowRight
            size={17}
            className="transition-transform group-hover:translate-x-1"
          />
        </div>
      </div>
    </Link>
  );
}

/* ============================================================
   FEATURED ARTICLE
============================================================ */

function FeaturedArticle({ blog }) {
  return (
    <Link
      to={`/blogs/${blog.slug}`}
      className="group grid overflow-hidden rounded-[24px] border border-[#DCEAE1] bg-white shadow-[0_15px_50px_rgba(6,78,59,0.08)] lg:grid-cols-[1.1fr_0.9fr]"
    >
      <div className="relative min-h-[300px] overflow-hidden bg-[#E8F0EB] sm:min-h-[400px]">
        <img
          src={getImage(blog)}
          alt={blog.title || "Featured travel article"}
          loading="eager"
          onError={(event) => {
            event.currentTarget.onerror = null;
            event.currentTarget.src = blogImg;
          }}
          className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
        />

        <div className="absolute inset-0 bg-gradient-to-t from-[#064E3B]/35 to-transparent" />

        <div className="absolute left-6 top-6 flex items-center gap-2 rounded-full bg-[#168B43] px-4 py-2 text-[11px] font-extrabold text-white">
          <Sparkles size={14} />
          Featured Story
        </div>
      </div>

      <div className="flex flex-col justify-center p-7 sm:p-10 lg:p-12">
        <div className="flex flex-wrap items-center gap-3">
          <span className="rounded-full bg-[#E8F7EE] px-4 py-2 text-[10px] font-extrabold uppercase tracking-wider text-[#168B43]">
            {blog.category?.name || "Travel Guide"}
          </span>

          <span className="flex items-center gap-2 text-xs text-[#8492A6]">
            <CalendarDays size={14} />
            {formatDate(blog.created_at)}
          </span>
        </div>

        <h3 className="mt-6 text-[28px] font-extrabold leading-[1.25] tracking-[-0.035em] text-[#17324D] transition group-hover:text-[#168B43] sm:text-[36px]">
          {blog.title}
        </h3>

        <p className="mt-5 line-clamp-4 text-sm leading-8 text-[#718096]">
          {getDescription(blog)}
        </p>

        <div className="mt-8 inline-flex w-fit items-center gap-3 rounded-xl bg-[#168B43] px-6 py-3.5 text-sm font-extrabold text-white transition group-hover:bg-[#087D37]">
          Read Featured Article
          <ArrowRight size={17} />
        </div>
      </div>
    </Link>
  );
}

/* ============================================================
   LOADING SKELETON
============================================================ */

function BlogSkeleton() {
  return (
    <div className="overflow-hidden rounded-[20px] border border-[#E3ECE6] bg-white">
      <div className="h-[240px] animate-pulse bg-[#E4EFE8]" />

      <div className="space-y-4 p-7">
        <div className="h-3 w-28 animate-pulse rounded bg-[#E4EFE8]" />
        <div className="h-6 w-full animate-pulse rounded bg-[#E4EFE8]" />
        <div className="h-6 w-4/5 animate-pulse rounded bg-[#E4EFE8]" />
        <div className="h-3 w-full animate-pulse rounded bg-[#EDF4EF]" />
        <div className="h-3 w-3/4 animate-pulse rounded bg-[#EDF4EF]" />
      </div>
    </div>
  );
}

/* ============================================================
   MAIN PAGE
============================================================ */

export default function Blogs() {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [search, setSearch] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");

  const [retryKey, setRetryKey] = useState(0);

  /* ==========================================================
     FETCH BLOGS
  ========================================================== */

  useEffect(() => {
    let active = true;

    const controller = new AbortController();

    async function fetchBlogs() {
      setLoading(true);
      setError("");

      try {
        const response = await axios.get(`${API_URL}/blogs`, {
          signal: controller.signal,
        });

        if (!active) return;

        const data = getBlogsFromResponse(response);

        setBlogs(data);
      } catch (err) {
        if (!active || axios.isCancel(err)) return;

        console.error("Unable to load blogs:", err);
        setError("Unable to load articles. Please try again.");
        setBlogs([]);
      } finally {
        if (active) setLoading(false);
      }
    }

    fetchBlogs();

    return () => {
      active = false;
      controller.abort();
    };
  }, [retryKey]);

  /* ==========================================================
     CATEGORIES
  ========================================================== */

  const categories = useMemo(() => {
    const unique = new Map();

    blogs.forEach((blog) => {
      if (blog.category?.id != null) {
        unique.set(String(blog.category.id), blog.category);
      }
    });

    return Array.from(unique.values());
  }, [blogs]);

  /* ==========================================================
     FILTER BLOGS
  ========================================================== */

  const filteredBlogs = useMemo(() => {
    const keyword = search.trim().toLowerCase();

    return blogs.filter((blog) => {
      const content = [
        blog.title,
        blog.excerpt,
        blog.description,
        blog.category?.name,
      ]
        .map((item) => plainText(item).toLowerCase())
        .join(" ");

      const matchesSearch =
        !keyword || content.includes(keyword);

      const matchesCategory =
        !selectedCategory ||
        String(blog.category?.id) === String(selectedCategory);

      return matchesSearch && matchesCategory;
    });
  }, [blogs, search, selectedCategory]);

  const clearFilters = () => {
    setSearch("");
    setSelectedCategory("");
  };

  const featuredBlog = filteredBlogs[0];

  const remainingBlogs = filteredBlogs.slice(1);

  const hasFilters = Boolean(search.trim() || selectedCategory);

  return (
    <>
      <Helmet>
        <title>
          Dubai Travel Blog & Hotel Guides | Dubai Budgetive Hotels
        </title>

        <meta
          name="description"
          content="Discover Dubai travel guides, hotel tips, destination inspiration and helpful advice for planning your next Dubai getaway."
        />

        <meta
          property="og:title"
          content="Dubai Travel Blog | Dubai Budgetive Hotels"
        />

        <meta
          property="og:description"
          content="Explore Dubai hotel guides, local experiences and travel inspiration."
        />

        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="robots" content="index, follow" />
      </Helmet>

      <main className="min-h-screen overflow-hidden bg-[#F5FAF7] text-[#17324D]">

        {/* =====================================================
            NEW SPLIT HERO SECTION
        ===================================================== */}


        {/* =====================================================
    BLOG PAGE BANNER
===================================================== */}

        <section className="relative isolate flex min-h-[420px] items-center overflow-hidden bg-[#064E3B] pt-24 sm:min-h-[480px]">
          {/* Background image */}
          <img
            src={blogImg}
            alt="Dubai travel blog banner"
            className="absolute inset-0 -z-20 h-full w-full object-cover object-center"
          />

          {/* Dark emerald overlay */}
          <div className="absolute inset-0 -z-10 bg-gradient-to-r from-[#032F28]/95 via-[#064E3B]/80 to-[#064E3B]/55" />

          {/* Bottom shadow */}
          <div className="absolute inset-0 -z-10 bg-gradient-to-t from-[#032F28]/40 via-transparent to-transparent" />

          <div className="mx-auto w-full max-w-[1536px] px-4 py-16 sm:px-6 lg:px-8">
            <div className="max-w-[780px]">

              {/* Breadcrumb */}
              <div className="mb-7 flex items-center gap-2 text-xs font-semibold text-white/75">
                <Link to="/" className="transition hover:text-white">
                  Home
                </Link>

                <ChevronRight size={14} />

                <span className="text-[#B6F2CB]">
                  Travel Blog
                </span>
              </div>

              {/* Label */}
              <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-white/25 bg-white/10 px-4 py-2 text-[11px] font-extrabold uppercase tracking-[0.16em] text-[#B6F2CB] backdrop-blur-sm">
                <BookOpen size={15} />
                The Dubai Travel Journal
              </div>

              {/* Heading */}
              <h1 className="text-[43px] font-extrabold leading-[1.12] tracking-[-0.045em] text-white sm:text-[58px] lg:text-[68px]">
                Explore Dubai.
                <br />
                <span className="text-[#A5E8B8]">
                  Discover New Stories.
                </span>
              </h1>

              {/* Description */}
              <p className="mt-6 max-w-[620px] text-[15px] leading-8 text-white/80 sm:text-base">
                Discover inspiring travel stories, Dubai destination
                guides, hotel recommendations, and useful tips to
                make your next journey unforgettable.
              </p>

              {/* Button */}
              <a
                href="#latest-articles"
                className="mt-8 inline-flex items-center gap-3 rounded-xl bg-[#168B43] px-7 py-4 text-sm font-extrabold text-white shadow-lg transition hover:-translate-y-0.5 hover:bg-[#087D37]"
              >
                Explore Our Articles
                <ArrowRight size={17} />
              </a>
            </div>
          </div>
        </section>


        {/* =====================================================
            SEARCH + CATEGORY FILTERS
        ===================================================== */}

        <section
          id="latest-articles"
          className="scroll-mt-24 bg-[#F5FAF7] py-16 sm:py-20"
        >
          <div className="mx-auto w-full max-w-[1536px] px-4 sm:px-6 lg:px-8">

            <SectionHeading
              eyebrow="Explore The Journal"
              title={
                <>
                  Latest Stories &{" "}
                  <span className="text-[#168B43]">
                    Travel Guides.
                  </span>
                </>
              }
              description="Browse travel stories, destination ideas, and useful guides to help plan your next Dubai trip."
              right={
                <div className="inline-flex items-center gap-3 rounded-xl border border-[#DCEAE1] bg-white px-5 py-4">
                  <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#E8F7EE] text-[#168B43]">
                    <BookOpen size={22} />
                  </div>

                  <div>
                    <p className="text-xl font-extrabold text-[#17324D]">
                      {loading ? "—" : blogs.length}
                    </p>
                    <p className="text-[11px] text-[#8492A6]">
                      Published Articles
                    </p>
                  </div>
                </div>
              }
            />

            {/* SEARCH PANEL */}
            <div className="mt-10 rounded-[20px] border border-[#DCEAE1] bg-white p-5 shadow-[0_8px_30px_rgba(6,78,59,0.04)] sm:p-6">
              <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">

                <div className="relative w-full lg:max-w-[450px]">
                  <Search
                    size={19}
                    className="absolute left-4 top-1/2 -translate-y-1/2 text-[#168B43]"
                  />

                  <input
                    type="search"
                    value={search}
                    onChange={(event) =>
                      setSearch(event.target.value)
                    }
                    placeholder="Search destinations, hotels, articles..."
                    aria-label="Search blog articles"
                    className="w-full rounded-xl border border-[#DCEAE1] bg-[#F8FBF9] py-4 pl-12 pr-5 text-sm text-[#17324D] outline-none transition placeholder:text-[#94A3B8] focus:border-[#168B43] focus:ring-4 focus:ring-[#168B43]/10"
                  />
                </div>

                <div className="flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setSelectedCategory("")}
                    className={`rounded-full px-5 py-3 text-xs font-extrabold transition ${selectedCategory === ""
                      ? "bg-[#168B43] text-white"
                      : "border border-[#DCEAE1] bg-white text-[#64748B] hover:border-[#168B43] hover:text-[#168B43]"
                      }`}
                  >
                    All Articles
                  </button>

                  {categories.map((category) => (
                    <button
                      key={category.id}
                      type="button"
                      onClick={() =>
                        setSelectedCategory(String(category.id))
                      }
                      className={`rounded-full px-5 py-3 text-xs font-extrabold transition ${String(selectedCategory) === String(category.id)
                        ? "bg-[#168B43] text-white"
                        : "border border-[#DCEAE1] bg-white text-[#64748B] hover:border-[#168B43] hover:text-[#168B43]"
                        }`}
                    >
                      {category.name}
                    </button>
                  ))}

                  {hasFilters && (
                    <button
                      type="button"
                      onClick={clearFilters}
                      className="inline-flex items-center gap-2 rounded-full px-4 py-3 text-xs font-extrabold text-[#168B43] hover:bg-[#E8F7EE]"
                    >
                      <X size={15} />
                      Clear
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* RESULT COUNT */}
            <div className="mb-7 mt-11 flex flex-wrap items-end justify-between gap-4">
              <div>
                <p className="text-[11px] font-extrabold uppercase tracking-[0.2em] text-[#168B43]">
                  {hasFilters ? "Your Search Results" : "Discover What's New"}
                </p>

                <h3 className="mt-2 text-[26px] font-extrabold tracking-tight text-[#17324D] sm:text-[32px]">
                  {hasFilters ? "Matching Articles" : "Featured & Latest Articles"}
                </h3>
              </div>

              {!loading && !error && (
                <p className="text-xs font-bold text-[#8492A6]">
                  {filteredBlogs.length}{" "}
                  {filteredBlogs.length === 1
                    ? "Article"
                    : "Articles"}{" "}
                  Found
                </p>
              )}
            </div>

            {/* LOADING */}
            {loading && (
              <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
                {[1, 2, 3, 4, 5, 6].map((item) => (
                  <BlogSkeleton key={item} />
                ))}
              </div>
            )}

            {/* ERROR */}
            {!loading && error && (
              <div className="rounded-[20px] border border-red-100 bg-white px-6 py-16 text-center">
                <AlertCircle
                  size={42}
                  className="mx-auto text-red-400"
                />

                <h3 className="mt-5 text-2xl font-extrabold">
                  Unable to Load Articles
                </h3>

                <p className="mt-3 text-sm text-[#718096]">
                  {error}
                </p>

                <button
                  type="button"
                  onClick={() => setRetryKey((value) => value + 1)}
                  className="mt-7 inline-flex items-center gap-2 rounded-xl bg-[#168B43] px-6 py-3 text-sm font-extrabold text-white"
                >
                  <RotateCcw size={16} />
                  Try Again
                </button>
              </div>
            )}

            {/* BLOG RESULTS */}
            {!loading && !error && filteredBlogs.length > 0 && (
              <>
                <FeaturedArticle blog={featuredBlog} />

                {remainingBlogs.length > 0 && (
                  <div className="mt-9 grid gap-6 md:grid-cols-2 xl:grid-cols-3">
                    {remainingBlogs.map((blog) => (
                      <BlogCard
                        key={blog.id ?? blog.slug}
                        blog={blog}
                      />
                    ))}
                  </div>
                )}
              </>
            )}

            {/* EMPTY STATE */}
            {!loading && !error && filteredBlogs.length === 0 && (
              <div className="rounded-[20px] border border-[#DCEAE1] bg-white px-6 py-16 text-center">
                <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-[#E8F7EE] text-[#168B43]">
                  <Search size={29} />
                </div>

                <h3 className="mt-5 text-2xl font-extrabold">
                  {hasFilters
                    ? "No Articles Found"
                    : "No Articles Available"}
                </h3>

                <p className="mx-auto mt-3 max-w-[430px] text-sm leading-7 text-[#718096]">
                  {hasFilters
                    ? "We couldn't find any articles matching your search. Try another keyword or category."
                    : "There are no published articles to display at the moment. Please check back soon."}
                </p>

                {hasFilters && (
                  <button
                    type="button"
                    onClick={clearFilters}
                    className="mt-7 inline-flex items-center gap-2 rounded-xl bg-[#168B43] px-6 py-3 text-sm font-extrabold text-white"
                  >
                    <X size={16} />
                    Clear Filters
                  </button>
                )}
              </div>
            )}
          </div>
        </section>

        {/* =====================================================
            HOTEL DISCOVERY CTA
        ===================================================== */}

        <section className="bg-white py-16 sm:py-20">
          <div className="mx-auto w-full max-w-[1536px] px-4 sm:px-6 lg:px-8">
            <div className="relative isolate overflow-hidden rounded-[24px] bg-[#064E3B] px-6 py-16 text-center sm:px-12 sm:py-20">
              <img
                src={HOTEL_IMAGE}
                alt=""
                loading="lazy"
                className="absolute inset-0 -z-20 h-full w-full object-cover"
              />

              <div className="absolute inset-0 -z-10 bg-gradient-to-r from-[#032F28]/95 via-[#064E3B]/90 to-[#064E3B]/75" />

              <div className="mx-auto max-w-[730px]">
                <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-full border border-white/25 bg-white/10 text-[#B6F2CB]">
                  <Hotel size={27} />
                </div>

                <p className="text-[11px] font-extrabold uppercase tracking-[0.2em] text-[#B6F2CB]">
                  From Inspiration To Destination
                </p>

                <h2 className="mt-4 text-[34px] font-extrabold leading-tight tracking-[-0.04em] !text-white sm:text-[49px]">
                  Inspired by Dubai?
                  <br />
                  Find Your Perfect Stay.
                </h2>

                <p className="mx-auto mt-5 max-w-[550px] text-sm leading-8 text-white/75">
                  Discover Dubai hotels, explore accommodation
                  options, and choose a stay that suits your
                  travel plans and budget.
                </p>

                <div className="mt-8 flex flex-wrap justify-center gap-3">
                  <Link
                    to="/hotels"
                    className="inline-flex items-center gap-3 rounded-xl bg-[#168B43] px-7 py-4 text-sm font-extrabold text-white transition hover:bg-[#087D37]"
                  >
                    Explore Dubai Hotels
                    <ArrowRight size={17} />
                  </Link>

                  <Link
                    to="/services"
                    className="inline-flex items-center gap-3 rounded-xl border border-white/30 bg-white/10 px-7 py-4 text-sm font-extrabold text-white backdrop-blur-md transition hover:bg-white/20"
                  >
                    Our Services
                    <ArrowUpRight size={17} />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
    </>
  );
}
